import React, { useState, useEffect, useCallback } from 'react';
import { AddressAutofill, config } from '@mapbox/search-js-react';
import AddressGeocodingServices from '../../../api/services/addressGeocoding';

const InputAddress = ({title, name, placeholder, icon = false, isRequired = false, errorEmpty = false, errorMessage = false, onChangeForm, valueInput, isRow = false, disabled = false}) => {
    const [addressDetails, setAddressDetails] = useState({
        addressLine: '',
        city: '',
        country: '',
        postalCode: '',
        longitude: '',
        latitude: '',
        mapboxStaticUrl: ''
    });
    const [token, setToken] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState(null);

    useEffect(() => {
        const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
        if (!accessToken) {
            console.error('Token Mapbox manquant');
            setValidationError('Configuration Mapbox manquante');
            return;
        }
        setToken(accessToken);
        config.accessToken = accessToken;
    }, []);

    // Fonction pour envoyer l'adresse au backend
    const sendAddressToBackend = async (address) => {
        try {
            setIsValidating(true);
            setValidationError(null);

            const response = await AddressGeocodingServices.updateAddressCoordinates(address);
                        
            if (response.status !== 200 || !response.data) {
                throw new Error('Erreur lors de la récupération des coordonnées');
            }

            const data = response.data;
            
            
            if (!data.localisation || !data.mapboxStaticUrl) {
                throw new Error('Données incomplètes reçues du serveur');
            }

            const addressData = {
                addressLine: address,
                longitude: data.localisation.coordinates[0],
                latitude: data.localisation.coordinates[1],
                mapboxStaticUrl: data.mapboxStaticUrl,
                city: '',
                country: '',
                postalCode: ''
            };

            setAddressDetails(addressData);
            onChangeForm(name, addressData);

        } catch (error) {
            console.error('Erreur lors de l\'envoi au backend:', error);
            
            // La fonction postRequestJson lance directement error.response.data.error
            // donc error est déjà le message d'erreur ou un objet d'erreur simple
            let errorMessage = 'Erreur lors de la récupération des coordonnées';
            
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error && error.message) {
                errorMessage = error.message;
            }
            
            setValidationError(errorMessage);
            
            // En cas d'erreur, créer un objet avec l'adresse mais sans coordonnées
            const fallbackData = {
                addressLine: address,
                longitude: '',
                latitude: '',
                mapboxStaticUrl: '',
                city: '',
                country: '',
                postalCode: ''
            };
            
            setAddressDetails(fallbackData);
            onChangeForm(name, fallbackData);
        } finally {
            setIsValidating(false);
        }
    };

    const handleRetrieve = useCallback((res) => {
        try {
            const feature = res.features[0];
            if (!feature) {
                throw new Error('Aucune adresse trouvée');
            }

            const { place_name } = feature.properties;
            
            if (!place_name) {
                throw new Error('Adresse invalide');
            }

            // Envoyer l'adresse au backend pour traitement
            sendAddressToBackend(place_name);

        } catch (error) {
            console.error('Erreur lors de la récupération de l\'adresse:', error);
            setValidationError(error.message);
        }
    }, [onChangeForm, name]);

    // Fonction pour gérer la saisie manuelle d'adresse
    const handleAddressSubmit = async () => {
        if (addressDetails.addressLine && addressDetails.addressLine.trim()) {
            await sendAddressToBackend(addressDetails.addressLine.trim());
        }
    };

    // Gérer la touche Entrée pour soumettre l'adresse
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddressSubmit();
        }
    };

    // Synchronisation avec la valeur externe
    useEffect(() => {
        if (valueInput && valueInput !== addressDetails.addressLine) {
            setAddressDetails(prevDetails => ({
                ...prevDetails,
                addressLine: valueInput
            }));
        }
    }, [valueInput]);

    return (
        <div className={`form__col address ${isRow ? 'row' : ''}`}>
            {title && (
                <p className="text-14 form__row__label">
                    {title} {isRequired && <span className="is__required">*</span>}
                </p>
            )}
            <div className={`access-container ${isRow ? 'col' : ''}`}>
                <div className={`form__row__input ${errorMessage || errorEmpty || validationError ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
                    {icon && <div className="form__row__icon">
                        <img src={icon} alt=""/>
                    </div>}
                    <AddressAutofill accessToken={token} onRetrieve={handleRetrieve} country="fr">
                        <input
                            type="text"
                            className="form__input__icon"
                            name={name}
                            placeholder={errorEmpty ? "Remplissez ce champ" : placeholder}
                            value={addressDetails.addressLine}
                            onChange={(e) => {
                                const newAddressLine = e.target.value;
                                setAddressDetails(prevDetails => ({
                                    ...prevDetails,
                                    addressLine: newAddressLine
                                }));
                                // Réinitialiser les coordonnées lors d'une nouvelle saisie
                                onChangeForm(name, {
                                    ...addressDetails,
                                    addressLine: newAddressLine,
                                    longitude: '',
                                    latitude: '',
                                    mapboxStaticUrl: ''
                                });
                            }}
                            onKeyPress={handleKeyPress}
                            onBlur={() => {
                                // Valider l'adresse quand l'utilisateur quitte le champ
                                if (addressDetails.addressLine && !addressDetails.longitude) {
                                    handleAddressSubmit();
                                }
                            }}
                            disabled={disabled || isValidating}
                            autoComplete="off"
                            id="mapbox-autofill"
                        />
                    </AddressAutofill>
                </div>
                {errorMessage && <div className="error-message">{errorMessage}</div>}
                {errorEmpty && <div className="error-message">Remplissez ce champ</div>}
                {validationError && <div className="error-message">{validationError}</div>}
                {isValidating && <div className="loading-message">Récupération des coordonnées...</div>}
            </div>
        </div>
    );
};

export default InputAddress;
